import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string } from "yup";
import { EHelperText } from "../../../../../utilitie/validation";
import {
  useCreateUser,
  useEditUserByUUID,
  useEditUserPasswordByUUID,
} from "../../../../../hook/query/userCRUD";
import { AppModalAlert, useModalState } from "../../../../global/Modal";
import { blurActiveElementBeforeOpeningModal } from "../../../../../hook/misc/modalState";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { SwitchInput, TextInput } from "../../../../global/Input";
import UserAvatar from "./UserAvatar";
import { CancelButton, SaveButton } from "../../../../global/Button";
import ResetPasswordModal from "../../../../global/Modal/ResetPasswordModal";
import ActionBar from "../../../../global/Widget/ActionBar";
import { useSettingAccessUserPageContextContext } from "../../../../../pages/Setting/Access/User";
import objectSameKeyValue from "../../../../../utilitie/objectSameKeyValue";

export interface IUserInformationProps {
  useInfoForm: UseFormReturn<IUserInformationFormValues, object>;
}

export interface IUserInformationFormValues {
  avatar: string;
  username: string;
  firstname: string;
  lastname: string;
  email: string;
  password?: string;
  is_active: number;
}

export const DUserInformationFormValues = {
  avatar: "",
  username: "",
  firstname: "",
  lastname: "",
  email: "",
};

export default function UserInformation({ useInfoForm, ...other }: IUserInformationProps) {
  const { uuid, defaultValues, setActionLog } = useSettingAccessUserPageContextContext();
  const { enqueueSnackbar } = useSnackbar();

  const [modalState, setModalState] = useModalState();

  const { mutateAsync: createUser, isLoading: createUserLoading } = useCreateUser();
  const { mutateAsync: editUserPasswordByUUID, isLoading: editUserPasswordByUUIDLoading } =
    useEditUserPasswordByUUID(uuid);
  const { mutateAsync: editUserByUUID, isLoading: editUserByUUIDLoading } = useEditUserByUUID(uuid);

  const {
    control,
    reset,
    handleSubmit,
    watch,
    formState: { isValid },
  } = useInfoForm;

  const isActiveValue = watch("is_active");

  const handleCancel = () => {
    if (uuid) {
      reset(defaultValues.useInfoForm);
    } else {
      setActionLog("cancel-create");
    }
  };

  const handleToggleModal = (openStatus = !modalState.open) => {
    blurActiveElementBeforeOpeningModal(openStatus);
    setModalState({ ...modalState, status: "", open: openStatus });
  };

  const handleResetPassword = async (values) => {
    let sortValues = {
      newPassword: values.new_password,
    };

    await editUserPasswordByUUID(sortValues)
      .then((data) => {
        setActionLog("edit");
        setModalState({ ...modalState, open: false, status: "success" });
      })
      .catch((error) => {
        setModalState({ ...modalState, open: false, status: "error" });
      });
  };

  const handleUpdate = async (values: FieldValues) => {
    if (uuid) {
      await editUserByUUID(values as IUserInformationFormValues)
        .then(() => {
          setActionLog("edit");
          enqueueSnackbar(EHelperText.UPDATED_SUCCESS, { variant: "success" });
        })
        .catch((error) => {
          enqueueSnackbar(EHelperText.AN_ERROR_OCCURRED, { variant: "error" });
        });
    } else {
      await createUser(values as IUserInformationFormValues)
        .then(() => {
          enqueueSnackbar("เพิ่มผู้ใช้สำเร็จ", { variant: "success" });
        })
        .catch((error) => {
          enqueueSnackbar(EHelperText.AN_ERROR_OCCURRED, { variant: "error" });
        });
    }
  };

  const genAutoComplete = (key) => {
    return { autoComplete: `${uuid ? key : "off"}` };
  };

  const isReadyToAction =
    !createUserLoading && !editUserByUUIDLoading && !editUserByUUIDLoading && isValid;

  return (
    <>
      {/* Modal Area */}
      <ResetPasswordModal
        loading={modalState.loading}
        title={modalState.title}
        open={modalState.open}
        onCancel={() => handleToggleModal(false)}
        onClose={() => handleToggleModal(false)}
        onConfirm={handleResetPassword}
      />
      <AppModalAlert
        title="รีเซ็ท รหัสผ่าน"
        open={!!modalState.status}
        status={modalState.status}
        onClose={() => handleToggleModal(false)}
      />
      <Stack
        height={"100%"}
        component="form"
        onSubmit={handleSubmit(handleUpdate, (error) => console.error(error))}
      >
        <Box flex={1} px={3} pt={2}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Stack alignItems={"center"} spacing={2}>
                <UserAvatar id="avatar" name="avatar" control={control} />
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    px: 2.5,
                    py: 0.75,
                    borderRadius: 6,
                    bgcolor: isActiveValue ? "success.50" : "grey.100",
                    border: "1px solid",
                    borderColor: isActiveValue ? "success.200" : "grey.300",
                    transition: "all 0.3s ease",
                  }}
                >
                  <SwitchInput id="is_active" name="is_active" control={control} />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      ml: 0.5,
                      color: isActiveValue ? "success.main" : "text.secondary",
                      transition: "color 0.3s",
                      userSelect: "none",
                    }}
                  >
                    {isActiveValue ? "เปิดใช้งาน" : "ระงับการใช้งาน"}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={6}>
              <TextInput
                disabled={!!uuid}
                id="username"
                name="username"
                label="ชื่อผู้ใช้"
                {...genAutoComplete("username")}
                control={control}
              />
            </Grid>
            <Grid item xs={6}>
              {uuid ? (
                <Button onClick={() => handleToggleModal(true)} fullWidth variant="outlined">
                  Reset Password
                </Button>
              ) : (
                <TextInput
                  type="password"
                  id="password"
                  name="password"
                  label="รหัสผ่าน"
                  {...genAutoComplete("password")}
                  control={control}
                />
              )}
            </Grid>
            <Grid item xs={6}>
              <TextInput
                id="firstname"
                name="firstname"
                label="ชื่อ"
                {...genAutoComplete("first-name")}
                control={control}
              />
            </Grid>
            <Grid item xs={6}>
              <TextInput
                id="lastname"
                name="lastname"
                label="นามสกุล"
                {...genAutoComplete("lastname")}
                control={control}
              />
            </Grid>
            <Grid item xs={6}>
              <TextInput
                type="email"
                id="email"
                name="email"
                label="อีเมล"
                {...genAutoComplete("email")}
                control={control}
              />
            </Grid>
          </Grid>
        </Box>
        <ActionBar>
          <CancelButton disabled={!isReadyToAction && !!uuid} onClick={handleCancel}>
            ยกเลิก
          </CancelButton>
          <SaveButton type="submit" disabled={!isReadyToAction}>
            บันทึก
          </SaveButton>
        </ActionBar>
      </Stack>
    </>
  );
}
